import { wasmConnector } from '@uwdata/vgplot';

let databasePromise;
const loadPromises = new Map();

const sqlString = (value) => {
  if (value == null) return 'NULL';
  return `'${String(value).replaceAll("'", "''")}'`;
};

const sqlValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'NULL';
  return sqlString(value);
};

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
};

export const initializeDuckDb = async () => {
  if (!databasePromise) {
    databasePromise = (async () => {
      const db = wasmConnector();
      const api = {
        query: (sql) => db.query({ type: 'json', sql }),
        exec: (sql) => db.query({ type: 'exec', sql }),
        tables: () =>
          db.query({
            type: 'json',
            sql: "SELECT table_name, table_type FROM information_schema.tables WHERE table_schema = 'main' ORDER BY table_name",
          }),
      };

      if (typeof window !== 'undefined') {
        window.doDqiDuckDB = api;
      }

      return api;
    })();
  }

  return databasePromise;
};

const batchInsertSql = (tableName, columns, rows) => {
  if (!rows.length) return '';

  const values = rows
    .map((row) => `(${columns.map((column) => sqlValue(row[column])).join(', ')})`)
    .join(',\n');

  return `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES\n${values};`;
};

const normalizePerformanceRows = (rows = []) =>
  rows.map((row) => ({
    sub_parameter: row.subParameter,
    compliance_score: toNumber(row.complianceScore),
    metrics_missed: toNumber(row.metricsMissed),
    fatal_parameters: toNumber(row.fatalParameters),
    actual_score: toNumber(row.actualScore),
    weightage: row.weightage ?? null,
    weightage_decimal: toNumber(row.weightageDecimal),
    weighted_score: toNumber(row.weightedScore),
    record_count: toNumber(row.recordCount),
  }));

const normalizeOverall = (overall = {}) => ({
  sub_parameter: overall.subParameter || 'Overall Weighted Score',
  weighted_score: toNumber(overall.weightedScore),
  sum_weighted_score: toNumber(overall.sumWeightedScore),
  total_weightage: overall.totalWeightage ?? null,
  overall_weightage: toNumber(overall.overallWeightage),
  formula: overall.formula || null,
});

const normalizeSummaryPayload = (payload) => ({
  title: payload.title || 'DO DQI',
  score: toNumber(payload.score),
  score_label: payload.scoreLabel || null,
  previous_period: payload.previousPeriod || null,
  previous_score: toNumber(payload.previousScore),
  previous_score_label: payload.previousScoreLabel || null,
  variation: toNumber(payload.variation),
  variation_label: payload.variationLabel || null,
  direction: payload.direction || null,
  calculation: payload.calculation || null,
  performance: payload.performance || null,
  capability: payload.capability || null,
  clientFeedback: payload.clientFeedback || null,
  responsiveness: payload.responsiveness || null,
});

const normalizeAccountsPayload = (payload) => ({
  title: payload.title || 'DO DQI Accounts',
  limit: toNumber(payload.limit),
  offset: toNumber(payload.offset),
  total_records: toNumber(payload.totalRecords),
  next_offset: toNumber(payload.nextOffset),
  calculation_performance: payload.calculation?.performance || null,
  calculation_do_dqi: payload.calculation?.doDqi || null,
  rows: (payload.data || []).map((row) => ({
    period: row.period,
    customer_id: row.customerId,
    customer_name: row.customerName,
    region_name: row.regionName,
    dqi_score: toNumber(row.dqiScore),
    performance: toNumber(row.performance),
    capability: toNumber(row.capability),
    client_feedback: toNumber(row.clientFeedback),
    responsiveness: toNumber(row.responsiveness),
    escalation_open: toNumber(row.escalationOpen),
    escalation_closed: toNumber(row.escalationClosed),
    performance_record_count: toNumber(row.performanceRecordCount),
    dqi_label: row.dqiLabel,
    performance_label: row.performanceLabel,
    capability_label: row.capabilityLabel,
    client_feedback_label: row.clientFeedbackLabel,
    responsiveness_label: row.responsivenessLabel,
  })),
});

const normalizePerformancePayload = (payload) => ({
  title: payload.title || 'A) Performance',
  rows: payload.data || [],
  overall: payload.overall || {},
  normalizedRows: normalizePerformanceRows(payload.data || []),
  normalizedOverall: normalizeOverall(payload.overall || {}),
});

const performanceColumns = [
  'sub_parameter',
  'compliance_score',
  'metrics_missed',
  'fatal_parameters',
  'actual_score',
  'weightage',
  'weightage_decimal',
  'weighted_score',
  'record_count',
];

const accountColumns = [
  'period',
  'customer_id',
  'customer_name',
  'region_name',
  'dqi_score',
  'performance',
  'capability',
  'client_feedback',
  'responsiveness',
  'escalation_open',
  'escalation_closed',
  'performance_record_count',
  'dqi_label',
  'performance_label',
  'capability_label',
  'client_feedback_label',
  'responsiveness_label',
];

const createPerformanceTables = async (db) => {
  await db.exec(`
    CREATE OR REPLACE TABLE do_dqi_performance (
      sub_parameter VARCHAR,
      compliance_score DOUBLE,
      metrics_missed DOUBLE,
      fatal_parameters DOUBLE,
      actual_score DOUBLE,
      weightage VARCHAR,
      weightage_decimal DOUBLE,
      weighted_score DOUBLE,
      record_count DOUBLE
    );

    CREATE OR REPLACE TABLE do_dqi_overall (
      sub_parameter VARCHAR,
      weighted_score DOUBLE,
      sum_weighted_score DOUBLE,
      total_weightage VARCHAR,
      overall_weightage DOUBLE,
      formula VARCHAR
    );
  `);
};

const insertPerformance = async (db, rows, overall) => {
  await createPerformanceTables(db);

  const performanceSql = batchInsertSql('do_dqi_performance', performanceColumns, rows);
  const overallSql = batchInsertSql(
    'do_dqi_overall',
    ['sub_parameter', 'weighted_score', 'sum_weighted_score', 'total_weightage', 'overall_weightage', 'formula'],
    [overall]
  );

  if (performanceSql) await db.exec(performanceSql);
  if (overallSql) await db.exec(overallSql);
};

const fetchJson = async (endpointUrl, errorLabel) => {
  const response = await fetch(endpointUrl);
  if (!response.ok) {
    throw new Error(`${errorLabel} returned ${response.status}`);
  }

  return response.json();
};

export const loadDoDqiSummaryIntoDuckDb = async (endpointUrl) => {
  const key = `summary:${endpointUrl}`;
  if (!loadPromises.has(key)) {
    loadPromises.set(
      key,
      (async () => {
        const payload = await fetchJson(endpointUrl, 'DO DQI summary API');
        const normalized = normalizeSummaryPayload(payload);
        const db = await initializeDuckDb();

        await db.exec(`
          CREATE OR REPLACE TABLE do_dqi_summary (
            title VARCHAR,
            score DOUBLE,
            score_label VARCHAR,
            previous_period VARCHAR,
            previous_score DOUBLE,
            previous_score_label VARCHAR,
            variation DOUBLE,
            variation_label VARCHAR,
            direction VARCHAR,
            calculation VARCHAR
          );
        `);

        const summarySql = batchInsertSql(
          'do_dqi_summary',
          [
            'title',
            'score',
            'score_label',
            'previous_period',
            'previous_score',
            'previous_score_label',
            'variation',
            'variation_label',
            'direction',
            'calculation',
          ],
          [normalized]
        );

        if (summarySql) await db.exec(summarySql);

        return { ...normalized, db };
      })()
    );
  }

  return loadPromises.get(key);
};

export const loadDoDqiAccountsIntoDuckDb = async (endpointUrl) => {
  const key = `accounts:${endpointUrl}`;
  if (!loadPromises.has(key)) {
    loadPromises.set(
      key,
      (async () => {
        const payload = await fetchJson(endpointUrl, 'DO DQI accounts API');
        const normalized = normalizeAccountsPayload(payload);
        const db = await initializeDuckDb();

        await db.exec(`
          CREATE OR REPLACE TABLE do_dqi_accounts (
            period VARCHAR,
            customer_id VARCHAR,
            customer_name VARCHAR,
            region_name VARCHAR,
            dqi_score DOUBLE,
            performance DOUBLE,
            capability DOUBLE,
            client_feedback DOUBLE,
            responsiveness DOUBLE,
            escalation_open DOUBLE,
            escalation_closed DOUBLE,
            performance_record_count DOUBLE,
            dqi_label VARCHAR,
            performance_label VARCHAR,
            capability_label VARCHAR,
            client_feedback_label VARCHAR,
            responsiveness_label VARCHAR
          );
        `);

        const accountsSql = batchInsertSql('do_dqi_accounts', accountColumns, normalized.rows);
        if (accountsSql) await db.exec(accountsSql);

        return { ...normalized, db };
      })()
    );
  }

  return loadPromises.get(key);
};

export const loadDoDqiPerformanceIntoDuckDb = async (endpointUrl) => {
  const key = `performance:${endpointUrl}`;
  if (!loadPromises.has(key)) {
    loadPromises.set(
      key,
      (async () => {
        const payload = await fetchJson(endpointUrl, 'DO DQI performance API');
        const normalized = normalizePerformancePayload(payload);
        const db = await initializeDuckDb();
        await insertPerformance(db, normalized.normalizedRows, normalized.normalizedOverall);
        return { ...normalized, db };
      })()
    );
  }

  return loadPromises.get(key);
};

export const loadDoDqiDetail = async (endpointUrl) => {
  const key = `detail:${endpointUrl}`;
  if (!loadPromises.has(key)) {
    loadPromises.set(key, fetchJson(endpointUrl, 'DO DQI detail API'));
  }

  return loadPromises.get(key);
};

export const resetDoDqiData = () => {
  loadPromises.clear();
};

export const queryDoDqiPerformance = async () => {
  const db = await initializeDuckDb();
  const [rows, overall] = await Promise.all([
    db.query(`
      SELECT sub_parameter, compliance_score, metrics_missed, fatal_parameters, actual_score, weightage, weighted_score, record_count
      FROM do_dqi_performance
      ORDER BY
        CASE sub_parameter
          WHEN 'SLAs' THEN 1
          WHEN 'Ops Rituals' THEN 2
          WHEN 'Guardrails' THEN 3
          WHEN 'Transactional Quality' THEN 4
          ELSE 5
        END
    `),
    db.query('SELECT sub_parameter, weighted_score, sum_weighted_score, total_weightage, overall_weightage, formula FROM do_dqi_overall LIMIT 1'),
  ]);

  return {
    rows,
    overall: overall[0] || null,
  };
};

export const queryDoDqiAccounts = async () => {
  const db = await initializeDuckDb();
  return db.query(`
    SELECT period, customer_id, customer_name, region_name, dqi_score, performance, capability, client_feedback,
      responsiveness, escalation_open, escalation_closed, performance_record_count, dqi_label, performance_label,
      capability_label, client_feedback_label, responsiveness_label
    FROM do_dqi_accounts
    ORDER BY dqi_score NULLS LAST, customer_name
  `);
};
